import { useState } from 'react';
import { TemplateSummary } from '@agency-smm/shared';
import {
  RegenModal, PickerModalContent, PickerHeader, PickerSearch,
  PickerList, PickerRow, PickerBadge, PickerFooter,
  RegenModalTitle, SelectionBarBtn,
} from './styled';

interface PickAndCycleModalProps {
  open: boolean;
  templates: TemplateSummary[];
  selectedPostIds: Set<string>;
  pickedTemplateIds: string[];
  setPickedTemplateIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  onApply: (cycleIds: string[]) => Promise<void>;
  onClose: () => void;
}

/**
 * Modal de "Pick & Cycle": el usuario elige un subset de templates y se
 * aplican en orden de inserción (round-robin) a los posts seleccionados.
 *
 * El search es local al modal. El estado del subset (pickedTemplateIds)
 * vive en el parent para sobrevivir al cierre/reapertura.
 */
export function PickAndCycleModal({
  open, templates, selectedPostIds, pickedTemplateIds,
  setPickedTemplateIds, onApply, onClose,
}: PickAndCycleModalProps) {
  const [search, setSearch] = useState('');

  if (!open) return null;

  const eligible = templates.filter((t) => t.isActive !== false);
  const filteredEligible = search.trim()
    ? eligible.filter((t) => t.name.toLowerCase().includes(search.trim().toLowerCase()))
    : eligible;
  const allFilteredChecked = filteredEligible.length > 0
    && filteredEligible.every((t) => pickedTemplateIds.includes(t.id));

  const togglePicked = (id: string) => {
    setPickedTemplateIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleApply = async () => {
    if (pickedTemplateIds.length === 0 || selectedPostIds.size === 0) return;
    await onApply(pickedTemplateIds);
    onClose();
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredChecked) {
      const filteredIds = new Set(filteredEligible.map((t) => t.id));
      setPickedTemplateIds((prev) => prev.filter((id) => !filteredIds.has(id)));
    } else {
      setPickedTemplateIds((prev) => {
        const have = new Set(prev);
        const added = filteredEligible.filter((t) => !have.has(t.id)).map((t) => t.id);
        return [...prev, ...added];
      });
    }
  };

  return (
    <RegenModal onClick={onClose}>
      <PickerModalContent onClick={(e) => e.stopPropagation()}>
        <PickerHeader>
          <RegenModalTitle style={{ marginBottom: 0, textAlign: 'left' }}>
            Pick & Cycle templates
          </RegenModalTitle>
          <span style={{ fontSize: 12, color: '#94A3B8' }}>
            {selectedPostIds.size} post{selectedPostIds.size === 1 ? '' : 's'}
          </span>
        </PickerHeader>

        <PickerHeader>
          <PickerSearch
            type="text"
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <SelectionBarBtn onClick={toggleSelectAllFiltered}>
            {allFilteredChecked ? 'Clear matches' : `Select all (${filteredEligible.length})`}
          </SelectionBarBtn>
        </PickerHeader>

        <PickerList>
          {filteredEligible.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
              No templates match.
            </div>
          )}
          {filteredEligible.map((t) => {
            const checked = pickedTemplateIds.includes(t.id);
            const orderIdx = pickedTemplateIds.indexOf(t.id);
            const kind: 'global' | 'user' | 'variant' =
              t.isGlobal ? 'global' : (t.parentTemplateId ? 'variant' : 'user');
            return (
              <PickerRow key={t.id} $checked={checked}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => togglePicked(t.id)}
                />
                <span title={t.id}>
                  <PickerBadge $kind={kind}>{kind}</PickerBadge>
                  {t.name}
                </span>
                <span style={{ fontSize: 11, color: '#94A3B8' }}>
                  {t.canvasWidth}×{t.canvasHeight}
                </span>
                <span style={{ fontSize: 11, color: checked ? '#3B82F6' : '#94A3B8', fontWeight: 600, textAlign: 'right' }}>
                  {checked ? `Cycle pos ${orderIdx + 1}` : ''}
                </span>
              </PickerRow>
            );
          })}
        </PickerList>

        <PickerFooter>
          <span>
            {pickedTemplateIds.length === 0
              ? 'Pick at least one template.'
              : selectedPostIds.size === 0
                ? 'Select posts to apply.'
                : `${pickedTemplateIds.length} template${pickedTemplateIds.length === 1 ? '' : 's'} cycled across ${selectedPostIds.size} post${selectedPostIds.size === 1 ? '' : 's'}.`}
          </span>
          <span style={{ display: 'flex', gap: 6 }}>
            <SelectionBarBtn onClick={onClose}>Cancel</SelectionBarBtn>
            <SelectionBarBtn
              onClick={handleApply}
              disabled={pickedTemplateIds.length === 0 || selectedPostIds.size === 0}
              style={{ background: '#3B82F6', color: 'white', borderColor: '#3B82F6' }}
            >
              Apply
            </SelectionBarBtn>
          </span>
        </PickerFooter>
      </PickerModalContent>
    </RegenModal>
  );
}
